'use client'

import { type FC } from 'react'

import { useRecommendationIntersection } from '../../../../cross-sell'
import {
  gtmDataLayerPushRecommendationTracking,
  RECOMMENDATION_WIDGET_PDP_OUT_OF_STOCK,
  RecommendationHorizontalSlider,
  RecommendationWidgetContainer,
  RecommendationWidgetProvider,
  RecommendationWidgetTitle,
  useGetRecommendationWidgetId,
  useSwrFetchRecommendationService,
} from '../../../../recommendation'
import { productAccessorGetRecommendationServiceId } from '../../../model/productAccessor/productAccessorGetRecommendationServiceId'
import { useProductPageContext } from '../../productPageContext'

import { useShouldRenderProductRecommendationWidgetOutOfStockSlider } from './useShouldRenderProductRecommendationAreaOutOfStockSlider'

type ProductRecommendationWidgetOutOfStockSliderProps = {
  className?: string
  itemsPerPage: number
}

export const ProductRecommendationWidgetOutOfStockSlider: FC<
  ProductRecommendationWidgetOutOfStockSliderProps
> = ({ className, itemsPerPage }) => {
  const { currentOffer, product } = useProductPageContext()
  const shouldRenderArea =
    useShouldRenderProductRecommendationWidgetOutOfStockSlider()
  const { handleIntersection, intersected } = useRecommendationIntersection()
  const sellerId = currentOffer?.seller?.sellerId ?? ''

  const serviceId = productAccessorGetRecommendationServiceId({
    isAvailable: false,
    isRx: false,
  })

  const { data, error } = useSwrFetchRecommendationService(
    intersected && shouldRenderArea
      ? {
          pid: product.upid,
          sellerid: sellerId,
          serviceId,
        }
      : undefined,
  )

  const widgetId = useGetRecommendationWidgetId(
    serviceId,
    RECOMMENDATION_WIDGET_PDP_OUT_OF_STOCK,
  )

  if (
    error ||
    !shouldRenderArea ||
    !widgetId ||
    data?.recommendations[widgetId]?.content.length === 0
  ) {
    return null
  }

  const widgetData = data?.recommendations[widgetId]

  return (
    <RecommendationWidgetProvider
      serviceId={serviceId}
      widgetData={widgetData}
      widgetId={widgetId}
    >
      <RecommendationWidgetContainer
        className={className}
        onIntersect={handleIntersection}
        widgetData={data}
        widgetId={widgetId}
      >
        <RecommendationWidgetTitle className="mb-2.5 text-xl tablet:text-2xl" />
        <RecommendationHorizontalSlider
          onGtmDataLayerPush={(widgetProductId, trackingToken) => {
            gtmDataLayerPushRecommendationTracking({
              productId: product.upid,
              trackingToken,
              widgetId,
              widgetProductId,
            })
          }}
          products={widgetData?.content}
          productsPerPage={itemsPerPage}
        />
      </RecommendationWidgetContainer>
    </RecommendationWidgetProvider>
  )
}
